import Vue from 'vue';
import map from 'lodash/map';
import cloneDeep from 'lodash/cloneDeep';
import { apolloClient } from '@/plugins/vue-apollo';
import { fetchCatalogItemsQuery, MRO_SEARCH_ITEMS, MRO_SEARCH_ITEMS_COUNT,
  MRO_CATALOG_GET_PRODUCT_ITEM, MRO_INSERT_INFORMATION_REQUEST, MRO_CATALOG_BY_PK, MRO_SEARCH_CATALOGS_TYPES,
  MRO_SEARCH_ORGANIZATIONS_COUNTRIES } from '@/store/graphql/queries/cat4mro';
import { MRO_SAVE_SEARCH_WORDS, MRO_SAVE_ITEM_VISIT } from '@/store/graphql/mutations/cat4mro';

const cat4mro = {
  namespaced: true,
  state: {
    search: {
      data: [],
      count: 0,
    },
    suggestions: [],
    catalogTypes: [],
    catalog: null,
    catalogItemsSearch: {
      data: [],
      count: 0,
      mdm: 0,
      public_catalog: 0,
      cross_reference: 0,
    },
    catalogProduct: null,
  },
  actions: {
    search_initial_state({ commit }) {
      commit('SET_CAT4MRO_SEARCH', {
        data: [],
        count: 0,
      });
    },
    async search({ commit, state, dispatch }, filters) {
      try {
        const searchData = {
          offset: filters.offset,
          limit: filters.limit,
          keywords: filters.query,
          organization_id: filters.organization_id,
          category_id: filters.category_id,
          catalog_type: filters.catalog_type,
          country: filters.country,
        };

        const searchQuery = {
          fetchPolicy: 'network-only',
          query: MRO_SEARCH_ITEMS,
          variables: searchData,
        };

        const result = await apolloClient.query(searchQuery);
        if (result && result.data) {
          const data = result.data;
          if (searchData.offset === 0) {
            delete searchData.catalog_type;
            if ('limit' in searchData) {
              delete searchData.limit;
            }
            if ('offset' in searchData) {
              delete searchData.offset;
            }
            dispatch('searchCount', searchData);
          }

          commit('SET_CAT4MRO_SEARCH', {
            data: data.data,
            count: data.all.aggregate.count,
          });
        } else {
          throw new Error('searchCatalogItems data is null');
        }
      } catch (error) {
        window.console.error(error);
      }

      return state.search;
    },
    async searchCount({ commit }, searchData) {
      try {
        const searchCountQuery = {
          query: MRO_SEARCH_ITEMS_COUNT,
          variables: {
            keywords: searchData.keywords,
            organization_id: searchData.organization_id,
            category_id: searchData.category_id,
          },
        };

        const result = await apolloClient.query(searchCountQuery);
        if (result && result.data) {
          commit('SET_CAT4MRO_SEARCH_COUNT', {
            mdm: result.data.mdm.aggregate.count,
            catalog: result.data.public_catalog.aggregate.count,
            cross_reference: result.data.cross_reference.aggregate.count,
          });
        }
      } catch (error) {
        window.console.error(error);
      }
    },
    async searchCatalogItems({ commit, state }, filters) {
      try {
        const searchQuery = {
          fetchPolicy: 'network-only',
          query: fetchCatalogItemsQuery,
          variables: {
            catalog: filters.catalog,
            offset: filters.offset,
            limit: filters.limit,
            keyword: filters.query,
            published_at_is_null: filters.published_at_is_null,
            price_gte: filters.price_gte,
            price_lte: filters.price_lte,
          },
        };
        const result = await apolloClient.query(searchQuery);
        if (result && result.data) {
          const data = result.data;
          commit('SET_CATALOG_SEARCH', {
            data: data.search_item,
            count: data.search_item_aggregate.aggregate.count,
          });
        } else {
          throw new Error('searchCatalogItems data is null');
        }
      } catch (error) {
        window.console.error(error);
      }

      return state.catalogItemsSearch;
    },
    async getProduct({ commit, state }, productId) {
      commit('SET_CATALOG_PRODUCT_DATA', {});
      try {
        const searchQuery = {
          query: MRO_CATALOG_GET_PRODUCT_ITEM,
          variables: {
            productId,
          },
        };
        const result = await apolloClient.query(searchQuery);
        if (result && result.data) {
          commit('SET_CATALOG_PRODUCT_DATA', {
            productItem: result.data.item_by_pk,
          });
        } else {
          commit('SET_CATALOG_PRODUCT_DATA', null);
        }
      } catch (error) {
        window.console.error(error);
      }

      return state.catalogProduct;
    },
    async newInformationRequest(store, informationRequest) {
      try {
        const insertMutation = {
          mutation: MRO_INSERT_INFORMATION_REQUEST,
          variables: {
            data: informationRequest,
          },
        };
        await apolloClient.mutate(insertMutation);
      } catch (error) {
        throw new Error(error);
      }
    },
    async getCatalog({ commit }, catalogId) {
      try {
        const insertMutation = {
          mutation: MRO_CATALOG_BY_PK,
          variables: {
            id: catalogId,
          },
        };
        const response = await apolloClient.mutate(insertMutation);
        if (response && response.data) {
          commit('SET_CATALOG', response.data.catalog_by_pk);
        }
      } catch (error) {
        throw new Error(error);
      }
    },
    async saveSearch(store, filters) {
      try {
        const insertMutation = {
          mutation: MRO_SAVE_SEARCH_WORDS,
          variables: {
            words: filters.query,
            offset: filters.offset,
            limit: filters.limit,
            organization_id: filters.organization_id,
            catalog_type: filters.catalog_type,
            category_id: Array.isArray(filters.category_id) ? filters.category_id[0] : null,
            country: filters.country,
          },
        };
        await apolloClient.mutate(insertMutation);
      } catch (error) {
        throw new Error(error);
      }
    },
    async saveItemVisit(store, itemId) {
      try {
        const insertMutation = {
          mutation: MRO_SAVE_ITEM_VISIT,
          variables: {
            itemId,
          },
        };
        await apolloClient.mutate(insertMutation);
      } catch (error) {
        throw new Error(error);
      }
    },
    async getCatalogsTypes({ commit, state }) {
      try {
        const getCatalogsTypeQuery = {
          query: MRO_SEARCH_CATALOGS_TYPES,
        };

        const response = await apolloClient.query(getCatalogsTypeQuery);
        if (response && response.data) {
          commit('SET_CATALOGS_TYPES', response.data.catalog_type);
        }
      } catch (error) {
        throw new Error(error);
      }

      return state.catalogTypes;
    },
    async getOrganizationsCountries({ commit, state }) {
      try {
        const getOrganizationsCountries = {
          query: MRO_SEARCH_ORGANIZATIONS_COUNTRIES,
        };

        const response = await apolloClient.query(getOrganizationsCountries);
        if (response && response.data) {
          const countryCodes = response.data.organization.map(item => item.code);
          commit('SET_ORGANIZATIONS_COUNTRIES', countryCodes);
        }
      } catch (error) {
        throw new Error(error);
      }

      return state.catalogCountries;
    },
  },
  mutations: {
    SET_CAT4MRO_SEARCH(state, { data, count }) {
      const mapData = map(data, (searchItem) => {
        const retVal = cloneDeep(searchItem);
        if (searchItem.catalog && searchItem.catalog[0] && searchItem.catalog[0].organization) {
          const organization = searchItem.catalog[0].organization;
          retVal.organizationId = organization.id;
          retVal.organizationName = organization.name;
          retVal.organizationTaxId = organization.tax_id;
        }
        return retVal;
      });

      Vue.set(state.search, 'data', mapData);
      Vue.set(state.search, 'count', count);
    },
    // eslint-disable-next-line camelcase
    SET_CAT4MRO_SEARCH_COUNT(state, { mdm, catalog, cross_reference }) {
      Vue.set(state.search, 'mdm', mdm);
      Vue.set(state.search, 'public_catalog', catalog);
      Vue.set(state.search, 'cross_reference', cross_reference);
    },
    SET_CATALOG_SEARCH(state, { data, count }) {
      Vue.set(state.catalogItemsSearch, 'data', data);
      Vue.set(state.catalogItemsSearch, 'count', count);
    },
    SET_CATALOG_PRODUCT_DATA(state, { productItem }) {
      Vue.set(state, 'catalogProduct', productItem);
    },
    SET_CATALOG(state, data) {
      Vue.set(state, 'catalog', data);
    },
    SET_CATALOGS_TYPES(state, data) {
      Vue.set(state, 'catalogTypes', data || []);
    },
    SET_ORGANIZATIONS_COUNTRIES(state, data) {
      Vue.set(state, 'organizationsCountries', data || []);
    },
  },
  getters: {
    catalogItemInfo: state => state.catalogProduct,
    catalogItemsSearchInfo: state => state.catalogItemsSearch,
    searchInfo: state => state.search,
    catalogInfo: state => state.catalog,
    catalogTypesInfo: state => state.catalogTypes,
    organizationsCountries: state => state.organizationsCountries,
  },
};

export default cat4mro;



// WEBPACK FOOTER //
// ./src/store/modules/buyer/cat4mro.js